/**
 * How big is this file, really (#1069 Story 4.1, Task 1).
 *
 * ⚠️ SYNC WITH: `server/entities/correspondence-message-file.entity.ts`
 * (`decodedBase64ByteLength`). This is the FRONTEND COPY, kept under the house
 * workaround for the deferred monorepo: the client cannot import `server/`, so
 * the function is duplicated and a parity test locks the two together
 * (`src/services/correspondence/__tests__/messageFileBytes.test.ts`, and
 * `src/constants/security.ts` does the same for the blocked-extension list).
 * If you change one, change both.
 *
 * WHY IT MATTERS HERE RATHER THAN BEING A DETAIL. D1a settled that 25 MB means
 * the user's OWN FILE, not what gets stored. The composer refuses a file at
 * selection and the server re-checks it on arrival; if those two disagree by so
 * much as a byte, a file the user was told was fine comes back refused, with no
 * way for them to tell why. One rule, two copies, one test holding them equal.
 */

/**
 * The DECODED length, in bytes, of a base64 payload, computed without
 * allocating the decoded buffer.
 *
 * Every 4 base64 characters carry 3 bytes; a trailing `=` or `==` means the
 * last group carried 2 or 1. Whitespace (which some clients insert every 76
 * characters) is not data and is discounted first.
 *
 * ⚠️ IT VALIDATES NOTHING. It does not check the charset, does not check that
 * the length is a multiple of 4, and does not reject a `data:` URI prefix. The
 * picker strips the prefix before calling this; `uploadSecurity` on the server
 * is what actually judges the content.
 *
 * ⚠️ NEVER NEGATIVE. Padding-only input (`"="`, `"=="`) would otherwise come
 * back as -1.
 */
export const decodedBase64ByteLength = (base64: string): number => {
  const clean = base64.replace(/\s/g, '');
  if (clean.length === 0) return 0;
  const padding = clean.endsWith('==') ? 2 : clean.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((clean.length * 3) / 4) - padding);
};

/**
 * The ceiling on the RAW base64 string for one file, before decoding.
 *
 * ⚠️ THIS IS THE SECOND OF D1a's TWO CHECKS, AND IT IS NOT THE SAME CHECK.
 * `decodedBase64ByteLength` strips whitespace before measuring, so a 1 MB photo
 * padded with thirty megabytes of newlines passes a 25 MB DECODED check and
 * still stores every padding byte: the row is thirty times the size the limit
 * described. Capping the raw string closes that, and it has to be a separate
 * constant because the two quantities are genuinely different.
 *
 * 25 MB of real bytes is ~33.4 MB of base64 (a 4:3 inflation). 36 MB leaves
 * room for legitimate line wrapping without leaving room for smuggling.
 */
export const MAX_RAW_PAYLOAD_CHARS_PER_FILE = 36 * 1024 * 1024;

/**
 * The same ceiling for the WHOLE message.
 *
 * ⚠️ ADDED CLIENT-SIDE AT CODE REVIEW RUN 2 (2026-09-13). The server grew this
 * ceiling in run 1 and the client did not, so six padded files uploaded 144 MB
 * and were then refused — for a set the person's own file browser calls 6 MB.
 * That is the punishment D1 forbids, and it arrived in the same pass that added
 * the type refusal to stop exactly that shape. It also broke the two-sided
 * contract `correspondenceFileLimits.ts` states in its header, which the parity
 * test now covers.
 *
 * 40 MB decoded is ~53.4 MB of base64; 54 leaves room for line wrapping.
 */
export const MAX_MESSAGE_RAW_CHARS = 54 * 1024 * 1024;

/** Strip a `data:<mime>;base64,` prefix if the platform handed us one. */
export const stripDataUriPrefix = (value: string): string => {
  const comma = value.indexOf(',');
  return value.startsWith('data:') && comma !== -1
    ? value.slice(comma + 1)
    : value;
};
